import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowUpRight, Clock, ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, CtaSection } from "../components/common";
import { BLOG_POSTS } from "../data/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = (post.related || []).map((s) => BLOG_POSTS.find((p) => p.slug === s)).filter(Boolean);

  return (
    <>
      <Seo title={`${post.title} | STERILTRON Insights`} description={post.excerpt} path={`/blog/${post.slug}`} />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 bg-brand-950 text-white overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-16 md:pb-20">
          <nav className="flex items-center gap-2 text-xs text-brand-300 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight size={12} />
            <Link to="/blog" className="hover:text-white">Blog</Link><ChevronRight size={12} />
            <span className="text-brand-100">{post.category}</span>
          </nav>
          <SectionLabel dark>{post.category}</SectionLabel>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-extrabold tracking-tighter leading-[1.02] text-balance">{post.title}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-brand-200">
            <span>{post.date}</span>
            <span className="text-brand-500">•</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* COVER */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 md:-mt-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden border border-slate-200 bg-white">
          <img src={post.image} alt={post.title} className="w-full h-[300px] md:h-[480px] object-cover" />
        </motion.div>
      </div>

      {/* BODY */}
      <article className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="font-heading text-xl md:text-2xl font-medium tracking-tight text-brand-950 leading-snug text-balance">{post.excerpt}</p>
          <div className="mt-8 space-y-6">
            {post.body.map((block, i) => {
              if (block.type === "h") return <h2 key={i} className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-950 pt-4">{block.text}</h2>;
              if (block.type === "img") return (
                <Reveal key={i} className="overflow-hidden border border-slate-200 my-8">
                  <img src={block.src} alt={block.alt} loading="lazy" className="w-full h-[260px] md:h-[400px] object-cover" />
                </Reveal>
              );
              return <p key={i} className="text-slate-700 text-base md:text-lg leading-relaxed">{block.text}</p>;
            })}
          </div>
          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-brand-800 hover:gap-3 transition-all">
              <ArrowLeft size={16} /> Back to Blog
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-brand-50/40 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionLabel>Related Articles</SectionLabel>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`} className="group flex items-center gap-6 bg-white border border-slate-200 hover:border-brand-800 transition-colors">
                  <img src={r.image} alt={r.title} loading="lazy" className="w-32 h-32 md:w-40 md:h-40 object-cover flex-shrink-0 transition-transform duration-700 group-hover:scale-105" />
                  <div className="py-4 pr-6">
                    <div className="text-xs uppercase tracking-widest text-brand-700 font-semibold">{r.category}</div>
                    <h3 className="mt-2 font-heading text-base md:text-lg font-semibold tracking-tight text-brand-950 leading-snug">{r.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800">Read <ArrowUpRight size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaSection />
    </>
  );
}
